import { AuthProvider, HttpError } from 'react-admin';
import { API_BASE } from '../utils/apiClient';

interface User {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  role: string | null;
  permissions: string[];
}

interface AuthProviders {
  google: boolean;
  github: boolean;
}

// Cache for current user
let cachedUser: User | null = null;

export const authProvider: AuthProvider = {
  /**
   * Login - redirect to OAuth provider
   * React Admin calls this, but we handle OAuth via redirect
   */
  login: async ({ provider }: { provider?: string }) => {
    // Redirect to OAuth provider, passing current origin so the API redirects back here
    const authProvider = provider || 'google';
    const returnTo = encodeURIComponent(window.location.origin);
    window.location.href = `${API_BASE}/admin/auth/${authProvider}?return_to=${returnTo}`;
    return Promise.resolve();
  },

  /**
   * Logout - call logout endpoint and redirect
   */
  logout: async () => {
    try {
      await fetch(`${API_BASE}/admin/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    cachedUser = null;
    return Promise.resolve();
  },

  /**
   * Check if user is authenticated
   */
  checkAuth: async () => {
    try {
      const response = await fetch(`${API_BASE}/admin/auth/me`, {
        credentials: 'include',
      });

      if (!response.ok) {
        cachedUser = null;
        throw new Error('Not authenticated');
      }

      const user = await response.json();
      cachedUser = user;
      return Promise.resolve();
    } catch (error) {
      cachedUser = null;
      throw error;
    }
  },

  /**
   * Check for authentication errors in API responses
   */
  checkError: async (error: HttpError) => {
    const status = error.status;
    if (status === 401 || status === 403) {
      cachedUser = null;
      throw new Error('Session expired');
    }
    return Promise.resolve();
  },

  /**
   * Get the current user identity
   */
  getIdentity: async () => {
    if (cachedUser) {
      return {
        id: cachedUser.id,
        fullName: cachedUser.name || cachedUser.email,
        avatar: cachedUser.avatarUrl || undefined,
      };
    }

    const response = await fetch(`${API_BASE}/admin/auth/me`, {
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Not authenticated');
    }

    const user = await response.json();
    cachedUser = user;

    return {
      id: user.id,
      fullName: user.name || user.email,
      avatar: user.avatarUrl || undefined,
    };
  },

  /**
   * Get user permissions for authorization
   */
  getPermissions: async () => {
    if (cachedUser) {
      return {
        role: cachedUser.role,
        permissions: cachedUser.permissions,
      };
    }

    try {
      const response = await fetch(`${API_BASE}/admin/auth/me`, {
        credentials: 'include',
      });

      if (!response.ok) {
        return { role: null, permissions: [] };
      }

      const user = await response.json();
      cachedUser = user;

      return {
        role: user.role,
        permissions: user.permissions,
      };
    } catch {
      return { role: null, permissions: [] };
    }
  },
};

/**
 * Get available OAuth providers
 */
export const getAuthProviders = async (): Promise<AuthProviders> => {
  try {
    const response = await fetch(`${API_BASE}/admin/auth/providers`);
    if (!response.ok) {
      return { google: false, github: false };
    }
    const data = await response.json();
    return {
      google: data.providers.includes('google'),
      github: data.providers.includes('github'),
    };
  } catch {
    return { google: false, github: false };
  }
};

/**
 * Check if user has a specific permission
 */
export const hasPermission = (permission: string): boolean => {
  if (!cachedUser) return false;
  if (cachedUser.role === 'super_admin') return true;
  return cachedUser.permissions.includes(permission);
};

/**
 * Check if user has at least the specified role level
 */
export const hasRole = (minimumRole: string): boolean => {
  if (!cachedUser || !cachedUser.role) return false;

  const roleHierarchy: Record<string, number> = {
    author: 1,
    editor: 2,
    admin: 3,
    super_admin: 4,
  };

  const userLevel = roleHierarchy[cachedUser.role] || 0;
  const requiredLevel = roleHierarchy[minimumRole] || 0;

  return userLevel >= requiredLevel;
};
